import DeviceTimeline from "@/app/activity/components/deviceTimeline/DeviceTimeline"

import SessionTimeline from "@/app/activity/components/timeline/sessionTimeline/SessionTimeline"
import ActivityDeviceInfo from "@/app/activity/detail/activityInfo/ActivityInfo"
import DevicePlayerInfo from "@/app/activity/detail/devicePlayerInfo/DevicePlayerInfo"
import { ActivityDetailModeProps } from "@/app/activity/detail/type"
import { createSessionStore } from "@/app/activity/store"
import { Session } from "@/types/session"
import classNames from "classnames"
import { add, sub } from "date-fns"
import { useCallback, useMemo, useRef } from "react"
import { usePlayerGroupsQuery, usePlayersQuery } from "@/hooks/api/useSettingQuery"

import OverlayScrollbar from "simplebar-react"

export default function ActivityDetailMode({
  activityInfo,
  sessionList,
  middleOfScrollBarSetter,
  activityTimelineContRef,
  groupedActivityData,
  playerSessionDataMap,
  teamDeviceList,
  playerList,
  onEditClick,
}: ActivityDetailModeProps) {
  const playerInfoRef = useRef<HTMLDivElement>(null)

  const {
    setSession,
    selectedSession,
    setTemporarySession,
    sessionGuideLine,
    temporarySession,
    setConfirmSaveDialogOpen,
  } = createSessionStore((state) => ({
    setSession: state.setSession,
    setTemporarySession: state.setTemporarySession,
    selectedSession: state.session,
    sessionGuideLine: state.sessionGuideLine,
    temporarySession: state.temporarySession,
    setConfirmSaveDialogOpen: state.setConfirmSaveDialogOpen,
  }))

  const { sessionDateStart, sessionDateFinish } = useMemo(() => {
    const sessionDateStart = sub(new Date(activityInfo?.dateStart || ""), { minutes: 15 })
    const sessionDateFinish = add(new Date(activityInfo?.dateFinish || ""), { minutes: 15 })

    return {
      sessionDateStart,
      sessionDateFinish,
    }
  }, [activityInfo])

  const { data: playerGroups } = usePlayerGroupsQuery()
  const { data: allPlayers } = usePlayersQuery()

  const getPlayerGroupName = useCallback(
    (idfPlayer: number) => {
      if (!allPlayers || !playerGroups?.teamPlayerGroups) return undefined
      const playerSetting = allPlayers.find((p) => p.idfPlayer === idfPlayer)
      if (!playerSetting?.idfPlayerGroup) return undefined
      return playerGroups.teamPlayerGroups.find(
        (g) => g.idfTeamPlayerGroup === playerSetting.idfPlayerGroup,
      )?.name
    },
    [allPlayers, playerGroups],
  )

  const getPlayerPosition = useCallback(
    (idfPlayer: number, currentPosition?: string) => {
      const participatedPosition = playerList?.find((p) => p.idfPlayer === idfPlayer)?.position
      if (participatedPosition) return participatedPosition
      if (currentPosition) return currentPosition
      return allPlayers?.find((p) => p.idfPlayer === idfPlayer)?.position ?? ""
    },
    [allPlayers, playerList],
  )

  const handlePlayerScrollEvent = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop
    const timelineTarget = document.querySelector(".simplebar-content-wrapper") as HTMLElement

    if (timelineTarget) {
      timelineTarget.scrollTop = scrollTop
    }
  }

  const handleTimelineScrollEvent = (e: React.UIEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const scrollLeft = e.currentTarget.scrollLeft
    const scrollTop = e.currentTarget.scrollTop
    const timelineTarget = document.querySelector(".SessionTimelineContWrapper")
    const playerInfoTarget = playerInfoRef.current

    if (timelineTarget) {
      timelineTarget.scrollLeft = scrollLeft
    }

    if (playerInfoTarget) {
      playerInfoTarget.scrollTop = scrollTop
    }

    middleOfScrollBarSetter()
  }

  // The timeline itself doesn't scroll vertically (overflow-y: hidden); forward wheel to
  // player-device-info, which drives the timeline back via handlePlayerScrollEvent.
  const handleTimelineWheelEvent = (e: React.WheelEvent<HTMLDivElement>) => {
    const playerInfoTarget = playerInfoRef.current
    if (!playerInfoTarget) return
    // deltaMode 1 = lines, 2 = pages; normalize to pixels so mouse wheels aren't sluggish.
    const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? playerInfoTarget.clientHeight : 1
    playerInfoTarget.scrollTop += e.deltaY * factor
  }

  return (
    <div className="activity-session-section">
      <ActivityDeviceInfo isCropping={false} onEditClick={onEditClick} />
      <SessionTimeline
        isLoading={!sessionList}
        currentItemList={sessionList || []}
        dateStart={sessionDateStart}
        dateFinish={sessionDateFinish}
        height={234}
        onBoxClick={(session) => {
          // 같은 세션 클릭 시 선택 해제
          if (selectedSession?.idfSession === (session as Session).idfSession) {
            setSession(undefined)
            setTemporarySession(undefined)

            return
          }

          // 다른 세션 클릭 시 - 변경 사항이 있으면 확인 다이얼로그 표시
          if (
            selectedSession &&
            temporarySession &&
            (temporarySession.dateStart !== selectedSession.dateStart ||
              temporarySession.dateFinish !== selectedSession.dateFinish)
          ) {
            setConfirmSaveDialogOpen(true)

            return
          }

          setSession(session as Session)
          setTemporarySession(session as Session)
        }}
      />

      <div className="player-device-info" ref={playerInfoRef} onScroll={handlePlayerScrollEvent}>
        {groupedActivityData?.map((groupData, index) => (
          <div key={index} className="player-device-info-inner">
            {groupData.idfData.map((idfData, idfDataIdx) => {
              const originalData = groupData.activityDataByIdfData?.[idfData]
              return (
                <DevicePlayerInfo
                  key={idfData}
                  className={classNames("device-player", {
                    matchedToActivity:
                      originalData?.matchedToActivity ?? groupData.matchedToActivity,
                  })}
                  activityData={{
                    ...(originalData || groupData),
                    idfData: idfData,
                    position: getPlayerPosition(
                      groupData.idfPlayer,
                      originalData?.position || groupData.position,
                    ),
                    devicePlayer: {
                      ...(originalData?.devicePlayer || groupData.devicePlayer),
                      nickname:
                        (originalData?.devicePlayer?.nickname ||
                          groupData.devicePlayer?.nickname ||
                          "") + (groupData.idfData.length > 1 ? ` (${idfDataIdx + 1})` : ""),
                    },
                  }}
                  teamDeviceList={teamDeviceList}
                  playerGroupName={getPlayerGroupName(groupData.idfPlayer)}
                />
              )
            })}
          </div>
        ))}
      </div>

      <OverlayScrollbar
        className="activity-device-timeline"
        autoHide
        ref={activityTimelineContRef}
        scrollableNodeProps={{
          onScroll: handleTimelineScrollEvent,
          onWheel: handleTimelineWheelEvent,
        }}>
        {groupedActivityData?.map((groupData, index) => (
          <div key={groupData.idfPlayer + "_" + index} className="player-device-timeline">
            {groupData.idfData.map((idfData) => {
              const originalData = groupData.activityDataByIdfData?.[idfData]

              return (
                <div className="device-timeline" key={idfData}>
                  <div className="device-timeline-guideline-cont">
                    <DeviceTimeline
                      className={classNames({
                        matchedToActivity:
                          originalData?.matchedToActivity ?? groupData.matchedToActivity,
                      })}
                      key={groupData.idfPlayer + "_" + index}
                      activityData={{
                        ...(originalData || groupData),
                        idfData: idfData,
                      }}
                      sessionDataList={playerSessionDataMap?.[idfData] || []}
                      hideSessionGuideline={!sessionGuideLine}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </OverlayScrollbar>
    </div>
  )
}
